import { useMutation } from '@apollo/client';
import type {
  UpdateInternalAuditInput,
  UpdateInternalAuditMutation,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { UpdateInternalAuditDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import { useUpdateInternalAuditTRPC } from './useUpdateInternalAuditTRPC';

export const useUpdateInternalAudit = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  // GraphQL mutation
  const [updateInternalAuditGraphQL, graphqlState] = useMutation(
    UpdateInternalAuditDocument,
    {
      update: (cache) => {
        evictField(cache, 'internal_audit_entity');
        evictField(cache, 'business_area');
      },
    }
  );

  // tRPC mutation
  const trpcMutation = useUpdateInternalAuditTRPC();

  const updateInternalAudit = async (
    variables: UpdateInternalAuditInput
  ): Promise<UpdateInternalAuditMutation> => {
    if (trpcEnabled) {
      return trpcMutation.updateInternalAudit({
        Id: variables.Id,
        Title: variables.Title ?? '',
        OriginalTimestamp: variables.OriginalTimestamp,
        Description: variables.Description,
        BusinessArea: variables.BusinessArea,
        BusinessAreaId: variables.BusinessAreaId,
        CustomAttributeData: variables.CustomAttributeData,
        OwnerUserIds: variables.OwnerUserIds ?? [],
        OwnerGroupIds: variables.OwnerGroupIds ?? [],
        ContributorUserIds: variables.ContributorUserIds ?? [],
        ContributorGroupIds: variables.ContributorGroupIds ?? [],
        ViewerUserIds: variables.ViewerUserIds,
        ViewerGroupIds: variables.ViewerGroupIds,
        TagTypeIds: variables.TagTypeIds ?? [],
        DepartmentTypeIds: variables.DepartmentTypeIds ?? [],
      });
    }

    const result = await updateInternalAuditGraphQL({
      variables: { Input: variables },
    });
    if (!result.data) {
      throw new Error('Failed to update internal audit');
    }

    return result.data;
  };

  if (trpcEnabled) {
    return {
      updateInternalAudit,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    updateInternalAudit,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
